import {Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography} from "@mui/material";
import {AreaEntry, AreaPanelSpec, DEFAULT_AREA_FEET, areaDetailPatches, areaEntries} from "../operation-areas.service";
import {OperationAreaEntryDto} from "../../../shared/dtos/estimate-tree-operation.dto";

const feetOptions = Array.from(Array(50).keys()).map(i => i + 1);

const withFeet = (feet: Record<string, number>, key: string, value: number | undefined) => {
  const updated = {...feet};
  if (value === undefined) delete updated[key]; else updated[key] = value;
  return updated;
};

export interface OperationAreaChecklistProps {
  spec: AreaPanelSpec;
  details: {[key: string]: string | number | OperationAreaEntryDto[] | undefined};
  disabled: boolean;
  /** SQ-500 library preparation: new areas start unknown, feet can be Unknown, nothing checked is unknown. */
  factsOnly?: boolean;
  /** Receives the details patch (area array + legacy mirror keys) to merge. */
  onPatches: (patches: {[key: string]: string | number | OperationAreaEntryDto[] | undefined}) => void;
}

/**
 * The AC-267 area checklist for Canopy Clearance ("Clearance Areas") and
 * Crown Reduction ("Reduction Areas"): check the areas the work applies to;
 * each checked area shows the spec's feet dropdowns, and the last row is a
 * free-text custom area. Matches the Android OperationAreaPanel behavior and
 * data contract.
 */
export const OperationAreaChecklist = ({spec, details, disabled, factsOnly = false, onPatches}: OperationAreaChecklistProps) => {
  const entries = areaEntries(spec, details);
  const commit = (updated: AreaEntry[]) => onPatches(areaDetailPatches(spec, updated, factsOnly));

  const defaultFeet: Record<string, number> = factsOnly ? {} : Object.fromEntries(spec.valueFields.map(field => [field.key, DEFAULT_AREA_FEET]));

  // Custom row: the first stored area outside the fixed list is the custom
  // entry. Updates address it by index so a stray duplicate written by
  // another client is left untouched rather than silently merged.
  const customIndex = entries.findIndex(entry => !spec.areas.includes(entry.area));
  const custom = customIndex >= 0 ? entries[customIndex] : undefined;

  // aria-labelledby splits on whitespace, so the id must not carry the area's spaces.
  const feetLabelId = (entry: AreaEntry, key: string) => `${spec.detailsKey}-${entry.area.replace(/\W+/g, '-')}-${key}`;
  const feetSelects = (entry: AreaEntry, onFeetChanged: (key: string, feet: number | undefined) => void) => (
    <div style={{display: 'flex', gap: '10px', paddingLeft: '32px'}}>
      {spec.valueFields.map(field => (
        <FormControl key={field.key} fullWidth size="small">
          <InputLabel id={feetLabelId(entry, field.key)}>{field.label}</InputLabel>
          <Select
            labelId={feetLabelId(entry, field.key)}
            disabled={disabled}
            label={field.label}
            value={entry.feet[field.key] ?? ''}
            onChange={e => onFeetChanged(field.key, e.target.value === '' ? undefined : Number(e.target.value))}
          >
            {factsOnly && <MenuItem value=""><em>Unknown</em></MenuItem>}
            {feetOptions.map(feet => <MenuItem value={feet} key={feet}>{feet} ft</MenuItem>)}
          </Select>
        </FormControl>
      ))}
    </div>
  );

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}} data-review-field={spec.detailsKey}>
      <Typography variant="subtitle2">{spec.title}</Typography>
      {spec.areas.map(area => {
        const entry = entries.find(e => e.area === area);
        return (
          <div key={area}>
            <FormControlLabel
              label={area}
              control={
                <Checkbox
                  disabled={disabled}
                  checked={!!entry}
                  onChange={(_, checked) => commit(
                    checked
                      ? [...entries, {area, feet: defaultFeet}]
                      : entries.filter(e => e.area !== area)
                  )}
                />
              }
            />
            {entry && feetSelects(entry, (key, feet) => commit(
              entries.map(e => e.area === area ? {...e, feet: withFeet(e.feet, key, feet)} : e)
            ))}
          </div>
        );
      })}
      <div>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <Checkbox
            disabled={disabled}
            checked={!!custom}
            onChange={(_, checked) => commit(
              checked
                ? [...entries, {area: '', feet: defaultFeet}]
                : entries.filter((_, index) => index !== customIndex)
            )}
          />
          <TextField
            fullWidth
            size="small"
            label="Custom"
            disabled={disabled || !custom}
            value={custom?.area ?? ''}
            onChange={e => commit(
              entries.map((entry, index) => index === customIndex ? {...entry, area: e.target.value} : entry)
            )}
          />
        </div>
        {custom && feetSelects(custom, (key, feet) => commit(
          entries.map((entry, index) => index === customIndex ? {...entry, feet: withFeet(entry.feet, key, feet)} : entry)
        ))}
      </div>
    </div>
  );
}
